import React, { useEffect, useState } from "react";
import {
  Coins,
  ArrowDownLeft,
  ArrowUpRight,
  Gift,
  History,
  Sparkles,
} from "lucide-react";
import { useLazyGetRewardDashboardQuery } from "../../redux/actions/Customer/serviceCustomer";


export interface IRewardTransaction {
  transactionId: number;
  points: number;
  balanceBefore: number;
  balanceAfter: number;
  transactionType: "Earn" | "Redeem" | string;
  status: string;
  referenceNumber: string;
  remarks: string;
  createdOn: string;
}

export interface IRewardPointsResponse {
  availablePoints: number;
  lifetimeEarned: number;
  lifetimeRedeemed: number;
  lastEarnedOn: string;
  lastRedeemedOn: string;
  totalTransactions: number;
  transactions: IRewardTransaction[];
}

const RewardDashboardView: React.FC = () => {
  const [fetchRewards, { data, isLoading, isError }] =
    useLazyGetRewardDashboardQuery();

  const [selectedVoucher, setSelectedVoucher] = useState<number | null>(100);
  const [customPoints, setCustomPoints] = useState<string>("");

  useEffect(() => {
    fetchRewards();
  }, [fetchRewards]);

  const handleSelectVoucher = (points: number) => {
    setSelectedVoucher(points);
    setCustomPoints("");
  };

  const handleCustomPointsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomPoints(e.target.value);
    setSelectedVoucher(null);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#2e5b37]"></div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-red-50 text-red-600 p-6 rounded-3xl text-center max-w-xl mx-auto">
        <p className="font-semibold">Unable to fetch reward points.</p>
        <button
          onClick={() => fetchRewards()}
          className="mt-3 px-4 py-2 bg-[#2e5b37] text-white rounded-xl text-xs font-bold"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-2 sm:p-4 space-y-6 pb-20">
      {/* 1. TOP GREEN WALLET-STYLE BANNER */}
      <div className="bg-[#2e5b37] rounded-[28px] p-6 sm:p-8 text-white flex flex-col md:flex-row items-start md:items-center justify-between gap-6 shadow-sm">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-emerald-100 opacity-90">
            <Coins className="w-4 h-4" />
            <span>MY REWARD POINTS</span>
          </div>
          <p className="text-sm text-emerald-100/80 mt-1">Available balance</p>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-xl font-bold text-emerald-100">PTS</span>
            <span className="text-4xl font-extrabold tracking-tight">
              {data?.availablePoints?.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }) ?? "0.00"}
            </span>
          </div>
        </div>

        {/* Total Earned / Redeemed Badges */}
        <div className="flex items-center gap-3 w-full md:w-auto">
          <div className="flex-1 md:flex-initial bg-[#3b6d45]/70 border border-white/10 rounded-2xl px-5 py-3 min-w-[150px]">
            <div className="flex items-center gap-1.5 text-xs text-emerald-200 font-medium">
              <ArrowDownLeft className="w-3.5 h-3.5" />
              <span>Total earned</span>
            </div>
            <p className="text-base font-bold text-white mt-1">
              PTS {data?.lifetimeEarned?.toLocaleString() ?? "0.00"}
            </p>
          </div>

          <div className="flex-1 md:flex-initial bg-[#3b6d45]/70 border border-white/10 rounded-2xl px-5 py-3 min-w-[150px]">
            <div className="flex items-center gap-1.5 text-xs text-emerald-200 font-medium">
              <ArrowUpRight className="w-3.5 h-3.5" />
              <span>Total redeemed</span>
            </div>
            <p className="text-base font-bold text-white mt-1">
              PTS {data?.lifetimeRedeemed?.toLocaleString() ?? "0.00"}
            </p>
          </div>
        </div>
      </div>

      {/* 2. BOTTOM MAIN SECTION */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* LEFT: REDEEM POINTS FORM */}
        
        {/* RIGHT: RECENT ACTIVITY LIST */}
        <div className="lg:col-span-7 bg-white border border-gray-100 rounded-[28px] p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-800 flex items-center justify-center font-bold">
              <History className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Recent Activity</h2>
              <p className="text-xs text-gray-400">
                Your latest points transactions
              </p>
            </div>
          </div>

          {/* Transaction Activity Items */}
          <div className="space-y-3 max-h-[460px] overflow-y-auto pr-1">
            {data?.transactions?.length ? (
              data.transactions.map((tx) => {
                const isEarn = tx.transactionType === "Earn";
                return (
                  <div
                    key={tx.transactionId}
                    className="flex items-center justify-between p-3 rounded-2xl hover:bg-gray-50/80 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className={`w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 ${
                          isEarn
                            ? "bg-emerald-50 text-emerald-600"
                            : "bg-amber-50 text-amber-600"
                        }`}
                      >
                        {isEarn ? (
                          <ArrowDownLeft className="w-5 h-5" />
                        ) : (
                          <ArrowUpRight className="w-5 h-5" />
                        )}
                      </div>

                      <div className="min-w-0">
                        <p className="text-sm font-bold text-gray-900 truncate">
                          {isEarn ? "Points Earned" : "Points Redeemed"}
                        </p>
                        <p
                          className="text-xs text-gray-400 mt-0.5 truncate"
                          title={tx.remarks}
                        >
                          {tx.remarks || `Order #${tx.referenceNumber}`}
                        </p>
                        <p className="text-[11px] text-gray-400 mt-0.5">
                          {formatDate(tx.createdOn)}
                        </p>
                      </div>
                    </div>

                    <div className="text-right whitespace-nowrap pl-3 shrink-0">
                      <p
                        className={`text-sm font-extrabold ${
                          isEarn ? "text-emerald-600" : "text-gray-900"
                        }`}
                      >
                        {isEarn ? "+ " : "- "}
                        {tx.points.toFixed(2)} PTS
                      </p>
                      <p className="text-[11px] text-gray-400 mt-0.5">
                        Balance {tx.balanceAfter.toFixed(2)}
                      </p>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-center py-16 text-xs text-gray-400">
                No transactions found
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RewardDashboardView;